import AccordionSummary from '@mui/material/AccordionSummary';
import { ChevronDownIcon } from 'src/icons/ChevronDownIcon';
import { theme } from 'src/theme/mui-theme';
import { Accordion, AccordionDetails, Box, IconButton, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { MyProductIcon } from "src/icons/planner/MyProductIcon";
import { useGetMyProductsQuery } from "src/api/v2/user-custom-items-v2-ApiSlice";
import { PencilIcon } from "src/icons/quote/PencilIcon";
import { TrashIcon } from "src/icons/quote/TrashIcon";
import TintedSVG from "src/pages/PlannerPage/DeviceView/TintedSVG";
import { DeleteUserCustomItemDialog } from "src/pages/PlannerPage/DeviceView/UserCustomItem/DeleteUserCustomItemDialog";
import {
  AddOrEditUserCustomItemDialog
} from "src/pages/PlannerPage/DeviceView/UserCustomItem/AddOrEditUserCustomItemDialog";
import {
  mapMyProductItemToDraggable,
  NEW_CUSTOM_DEVICE_DRAG,
  NEW_NOTE_DRAG
} from "src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/draggableDevices";
import { DraggableListItem } from "src/pages/PlannerPage/DeviceView/hooks/DragAndDrop/DraggableListItem";
import { ts } from "src/utils/translate";
import { placeDevice } from "src/components/Planner/planner/deviceDrawing";
import { addPlaceDeviceAction } from "src/components/Planner/planner/planner";
import { MyProduct } from "src/model/api/v2/data-contracts";
import { CustomItemDrop, NoteAddToDevice, NoteDrop, NoteEdit, NoteEditOnDevice } from "src/pages/PlannerPage/PlannerPage";
import { SelectedDevice } from "src/api/planner-ui-slice";
import { NoteIcon } from "src/icons/planner/NoteIcon";
import { AddNoteDialog } from "src/pages/PlannerPage/Planner/dialogs/AddNoteDialog/AddNoteDialog";

const USER_CUSTOM_ITEM_KEY_PREFIX = 'user_custom_item_';

interface DeviceViewMyProductsProps {
    customItemDrop: CustomItemDrop | null;
    setCustomItemDrop: (drop: CustomItemDrop | null) => void;
    noteDrop: NoteDrop | null;
    setNoteDrop: (drop: NoteDrop | null) => void;
    noteEdit: NoteEdit | null;
    setNoteEdit: (edit: NoteEdit | null) => void;
    noteEditOnDevice: NoteEditOnDevice | null;
    setNoteEditOnDevice: (edit: NoteEditOnDevice | null) => void;
    noteAddToDevice: NoteAddToDevice | null;
    setNoteAddToDevice: (add: NoteAddToDevice | null) => void;
    selectedDevice: SelectedDevice;
}

export function DeviceViewMyProducts({
    customItemDrop,
    setCustomItemDrop,
    noteDrop,
    setNoteDrop,
    noteEdit,
    setNoteEdit,
    noteEditOnDevice,
    setNoteEditOnDevice,
    noteAddToDevice,
    setNoteAddToDevice,
    selectedDevice,
}: DeviceViewMyProductsProps) {

    const selectedDeviceProductNameId = selectedDevice.productNameId;

    const { data: myProducts } = useGetMyProductsQuery();
    const [ isDeleteOpen, setIsDeleteOpen ] = useState(false);
    const [ selectedProductId, setSelectedProductId ] = useState<any>();
    const [ selectedProductName, setSelectedProductName ] = useState<any>();
    const [ isProductDialogOpen, setIsProductDialogOpen ] = useState(false);
    const [ editingProductId, setEditingProductId ] = useState<number | null>(null);
    const [ newCustomItemId, setNewCustomItemId ] = useState<number | null>(null);

    const selectedCustomItemId = selectedDeviceProductNameId?.startsWith(USER_CUSTOM_ITEM_KEY_PREFIX)
        ? Number(selectedDeviceProductNameId.slice(USER_CUSTOM_ITEM_KEY_PREFIX.length))
        : null;

    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        setExpanded(selectedCustomItemId != null);
    }, [selectedDevice]);

    const openProductDialog = (productId: number | null = null) => {
        setEditingProductId(productId);
        setIsProductDialogOpen(true);
    };

    const closeProductDialog = (newItemId?: number) => {
        setIsProductDialogOpen(false);
        setEditingProductId(null);
        if (newItemId && customItemDrop) {
            setNewCustomItemId(newItemId);
        } else {
            setCustomItemDrop(null);
            setNewCustomItemId(null);
        }
    };

    const placeNewProductAtDropCoordinates = () => {
        if (!myProducts || !customItemDrop || !newCustomItemId) return;

        const newProduct = myProducts.products.find(
            (product: MyProduct) => product.userCustomItem.id === newCustomItemId
        );
        if (newProduct) {
            const draggable = mapMyProductItemToDraggable(newProduct);
            placeDevice(
                customItemDrop.x,
                customItemDrop.y,
                customItemDrop.roomId,
                draggable,
                addPlaceDeviceAction(true, null),
                true,
                null,
                customItemDrop.rot,
                undefined,
                undefined,
                true,
                false,
                undefined
            );
            setCustomItemDrop(null);
            setNewCustomItemId(null);
        }
    };

    useEffect(() => {
        if (!customItemDrop) return;
        openProductDialog();
    }, [customItemDrop]);

    useEffect(placeNewProductAtDropCoordinates, [myProducts, newCustomItemId, customItemDrop, setCustomItemDrop]);

    return (
        <>
            <Accordion
                key='myproducts'
                disableGutters
                elevation={0}
                square
                sx={{
                    border: 'none',
                    boxShadow: 'none',
                    mb: 0,
                    '&::before': {display: 'none'},
                }}
                expanded={expanded}
                onChange={(_, isExpanded) => setExpanded(isExpanded)}
            >
                <AccordionSummary
                    expandIcon={
                        <ChevronDownIcon
                            sx={{
                                color: theme.palette.primary.main,
                                fontSize: 28,
                            }}
                        />
                    }
                >
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="h6" color={theme.palette.primary.main}>
                                My Products
                            </Typography>
                        </Stack>
                    </Stack>
                </AccordionSummary>

                <AccordionDetails>
                    <DraggableListItem
                        dragData={NEW_CUSTOM_DEVICE_DRAG}
                        icon={<MyProductIcon/>}
                        label={ts('new_custom_item', 'New custom item')}
                        onClick={() => openProductDialog()}
                        sxOverride={{
                            cursor: 'grab',
                            userSelect: 'none',
                            backgroundColor: theme.palette.grey[200],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            '&:hover': {
                                backgroundColor: theme.palette.grey[300],
                            },
                            '&:active': {
                                cursor: 'grabbing',
                            },
                            borderRadius: 2.5,
                        }}
                    />

                    <DraggableListItem
                        dragData={NEW_NOTE_DRAG}
                        icon={<NoteIcon/>}
                        label={ts('new_note', 'New note')}
                        sxOverride={{
                            cursor: 'grab',
                            userSelect: 'none',
                            backgroundColor: theme.palette.grey[200],
                            border: `1px solid ${theme.palette.grey[300]}`,
                            '&:hover': {
                                backgroundColor: theme.palette.grey[300],
                            },
                            '&:active': {
                                cursor: 'grabbing',
                            },
                            borderRadius: 2.5,
                            marginTop: 1,
                        }}
                    />

                    <Box sx={{marginTop: 2}}>
                        { myProducts && (
                            myProducts.products.map((product: MyProduct) => (
                                <DraggableListItem
                                    key={product.userCustomItem.id}
                                    dragData={mapMyProductItemToDraggable(product)}
                                    icon={<TintedSVG svg={product.icon_svg} color={product.icon_color ?? '#000000'} />}
                                    label={product.userCustomItem.product_name}
                                    selected={product.userCustomItem.id === selectedCustomItemId}
                                >
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <IconButton
                                            size="small"
                                            onClick={() => openProductDialog(product.userCustomItem.id)}
                                        >
                                            <PencilIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedProductId(product.userCustomItem.id);
                                                setSelectedProductName(product.userCustomItem.product_name);
                                                setIsDeleteOpen(true)
                                            }}
                                        >
                                            <TrashIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </DraggableListItem>
                            ))
                        )}
                    </Box>
                </AccordionDetails>
            </Accordion>

            {isDeleteOpen && (
                <DeleteUserCustomItemDialog
                    onClose={() => setIsDeleteOpen(false)}
                    productId={selectedProductId}
                    productName={selectedProductName}
                />
            )}

            {isProductDialogOpen && (
                <AddOrEditUserCustomItemDialog
                    productId={editingProductId}
                    onClose={closeProductDialog}
                />
            )}

            {noteDrop && (
                <AddNoteDialog
                    noteDrop={noteDrop}
                    onClose={() => setNoteDrop(null)}
                />
            )}

            {noteEdit && (
                <AddNoteDialog
                    noteEdit={noteEdit}
                    onClose={() => setNoteEdit(null)}
                />
            )}

            {noteEditOnDevice && (
                <AddNoteDialog
                    noteEditOnDevice={noteEditOnDevice}
                    onClose={() => setNoteEditOnDevice(null)}
                />
            )}

            {noteAddToDevice && (
                <AddNoteDialog
                    noteAddToDevice={noteAddToDevice}
                    onClose={() => setNoteAddToDevice(null)}
                />
            )}
        </>
    )
}